import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLazyQuery, useMutation } from "@apollo/client";
import gql from 'graphql-tag';

import { AUTH_URL, getUrl, zitadelRoutes } from "../../../includes/routes";
import { API_BASE_URL } from "../../../../config/config";
import { FAILED_AUTHENTICATION_PARAM } from "../../../includes/constants";
import { useZitadel, ZitadelSession } from "../../../contexts/ZitadelContext";

type ZitadelGetSessionResponse = {
    zitadelGetSession: ZitadelSession,
}

type ZitadelGetSessionVariables = {
    sessionId: string;
    sessionToken: string;
}

type ZitadelLoginWithIdpResponse = {
    zitadelLoginWithIdpV2: ZitadelSession,
}

type ZitadelLoginWithIdpVariables = {
    intentId: string;
    intentToken: string;
}

const Landing = () => {
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();
    const id = searchParams.get(zitadelRoutes.LANDING.params.ID) ?? "";
    const token = searchParams.get(zitadelRoutes.LANDING.params.TOKEN) ?? "";
    const isRedirectDisabled = searchParams.has(zitadelRoutes.LANDING.params.IS_REDIRECT_DISABLED);
    const backUrl = searchParams.get(zitadelRoutes.LANDING.params.BACK_URL) ?? "";
    const resolutionType = searchParams.get(zitadelRoutes.LANDING.params.RESOLUTION_TYPE) as ZitadelLandingResolutionType | "" ?? "";

    const isAuthenticationInFlight = useRef(false);
    const [isAuthenticationComplete, setIsAuthenticationComplete] = useState(false);

    const { setLoginInfo, setSession, clearSession } = useZitadel();

    const [zitadelGetSession] =
        useLazyQuery<ZitadelGetSessionResponse, ZitadelGetSessionVariables>(ZITADEL_GET_SESSION);

    const [zitadelLoginWithIdp] =
        useMutation<ZitadelLoginWithIdpResponse, ZitadelLoginWithIdpVariables>(ZITADEL_LOGIN_WITH_IDP);

    useEffect(function authenticateAndRedirectUser() {
        if (!id || !token || !resolutionType) {
            navigate(`${getUrl(AUTH_URL)}?${FAILED_AUTHENTICATION_PARAM}`);
            return;
        }

        /** Strict prevention of accidental double ups */
        if (isAuthenticationInFlight.current) return;
        isAuthenticationInFlight.current = true;

        const resolveLanding = (session: ZitadelSession) => {
            const isMfaRegistrationRequired = session.metadata.isMfaRegistrationRequired;
            const isTotpChallengeRequired = session.metadata.isTotpChallengeRequired;
            const isOtpEmailChallengeRequired = session.metadata.isOtpEmailChallengeRequired;

            /** Redirect to MFA routes when necessary */
            if (isMfaRegistrationRequired || isTotpChallengeRequired || isOtpEmailChallengeRequired) {
                setLoginInfo({ isRedirectDisabled, backUrl });
                setSession(session);
                setIsAuthenticationComplete(true);
                navigate(getUrl(zitadelRoutes.MFA_DISPATCH.route));
                return;
            }

            setIsAuthenticationComplete(true);

            if (!isRedirectDisabled) {
                let url = API_BASE_URL + "/auth/login-redirect";
                if (backUrl) url += "?BackURL=" + backUrl;
                window.location.href = url;
            }
        }

        switch (resolutionType) {
            case "password":
                zitadelGetSession({
                    variables: {
                        sessionId: id,
                        sessionToken: token,
                    },
                    fetchPolicy: 'network-only',
                }).then(({ data }) => {
                    if (!data) throw new Error("Login attempt did not return a valid response.");
                    resolveLanding(data.zitadelGetSession);
                }).catch(() => {
                    clearSession();
                    navigate(`${getUrl(AUTH_URL)}?${FAILED_AUTHENTICATION_PARAM}`);
                });
                break;
            case "idp-intent":
                zitadelLoginWithIdp({
                    variables: {
                        intentId: id,
                        intentToken: token,
                    },
                    fetchPolicy: 'network-only',
                }).then(({ data }) => {
                    if (!data) throw new Error("Login attempt did not return a valid response.");
                    resolveLanding(data.zitadelLoginWithIdpV2);
                }).catch(() => {
                    clearSession();
                    navigate(`${getUrl(AUTH_URL)}?${FAILED_AUTHENTICATION_PARAM}`);
                });
                break;
            default:
                throw new Error("Unknown resolution type.");
        }
    }, [
        backUrl,
        clearSession,
        id,
        token,
        isRedirectDisabled,
        navigate,
        setLoginInfo,
        setSession,
        zitadelLoginWithIdp,
        resolutionType,
        zitadelGetSession,
    ]);

    return (
        <div>
            {isAuthenticationComplete ?
                "Authentication complete," + (isRedirectDisabled ? " you may now close this window" : "") :
                "Completing authentication..."
            }
        </div>
    );
};

const ZITADEL_GET_SESSION = gql`
    query zitadelGetSession($sessionId: String!, $sessionToken: String!) {
        zitadelGetSession(sessionId: $sessionId, sessionToken: $sessionToken) {
            id
            token
            metadata {
                userEmail
                isMfaRegistrationRequired
                isTotpChallengeRequired
                isOtpEmailChallengeRequired
                isTotpAllowed
                isOtpEmailAllowed
                isSkipAllowed
            }
        }
    }
`;

const ZITADEL_LOGIN_WITH_IDP = gql`
    mutation zitadelLoginWithIdp($intentId: String!, $intentToken: String!) {
        zitadelLoginWithIdpV2(intentId: $intentId, intentToken: $intentToken) {
            id
            token
            metadata {
                userEmail
                isMfaRegistrationRequired
                isTotpChallengeRequired
                isOtpEmailChallengeRequired
                isTotpAllowed
                isOtpEmailAllowed
                isSkipAllowed
            }
        }
    }
`;

export default Landing;
